import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

const AUTH_TIPOS = [
  { value: 'salida', label: 'Salida' },
  { value: 'medicacion', label: 'Medicacion' },
  { value: 'foto', label: 'Fotografia' },
  { value: 'otro', label: 'Otro' },
];

export default function AdminPortal() {
  const { token } = useAuth();
  const [tab, setTab] = useState('diario');
  const [eventos, setEventos] = useState([]);
  const [filterEvento, setFilterEvento] = useState('');
  const [loading, setLoading] = useState(true);

  // Diario state
  const [entries, setEntries] = useState([]);
  const [showDiarioForm, setShowDiarioForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [diarioForm, setDiarioForm] = useState({
    eventoId: '', eventoNombre: '', fecha: new Date().toISOString().slice(0, 10),
    titulo: '', contenido: '', publicado: true,
  });
  const [savingDiario, setSavingDiario] = useState(false);

  // Autorizaciones state
  const [autorizaciones, setAutorizaciones] = useState([]);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [authForm, setAuthForm] = useState({
    eventoId: '', eventoNombre: '', titulo: '', descripcion: '', tipo: 'otro', obligatoria: false,
  });
  const [savingAuth, setSavingAuth] = useState(false);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [evts, d, a] = await Promise.all([
        api.getAdminEvents(token),
        api.getDiarioAdmin(token),
        api.getAutorizacionesAdmin(token),
      ]);
      setEventos(evts.events || []);
      setEntries(d.entries || []);
      setAutorizaciones(a.autorizaciones || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  // ── Diario handlers ──
  function openDiarioForm(entry = null) {
    if (entry) {
      setEditingEntry(entry);
      setDiarioForm({
        eventoId: entry.eventoId, eventoNombre: entry.eventoNombre || '',
        fecha: entry.fecha, titulo: entry.titulo, contenido: entry.contenido,
        publicado: entry.publicado !== false,
      });
    } else {
      setEditingEntry(null);
      setDiarioForm({
        eventoId: '', eventoNombre: '', fecha: new Date().toISOString().slice(0, 10),
        titulo: '', contenido: '', publicado: true,
      });
    }
    setShowDiarioForm(true);
  }

  async function handleDiarioSubmit(e) {
    e.preventDefault();
    if (!diarioForm.eventoId || !diarioForm.titulo || !diarioForm.contenido) {
      alert('Rellena evento, titulo y contenido');
      return;
    }
    setSavingDiario(true);
    try {
      if (editingEntry) {
        await api.updateDiarioEntry(token, editingEntry.id, {
          titulo: diarioForm.titulo,
          contenido: diarioForm.contenido,
          fecha: diarioForm.fecha,
          publicado: diarioForm.publicado,
        });
      } else {
        await api.createDiarioEntry(token, diarioForm);
      }
      setShowDiarioForm(false);
      setEditingEntry(null);
      loadAll();
    } catch (err) { alert('Error: ' + err.message); }
    setSavingDiario(false);
  }

  async function deleteDiario(id) {
    if (!window.confirm('Eliminar esta entrada del diario?')) return;
    try {
      await api.deleteDiarioEntry(token, id);
      loadAll();
    } catch (e) { alert('Error: ' + e.message); }
  }

  async function togglePublicado(entry) {
    try {
      await api.updateDiarioEntry(token, entry.id, { publicado: !entry.publicado });
      loadAll();
    } catch (e) { alert('Error: ' + e.message); }
  }

  // ── Autorizaciones handlers ──
  async function handleAuthSubmit(e) {
    e.preventDefault();
    if (!authForm.eventoId || !authForm.titulo) {
      alert('Rellena evento y titulo');
      return;
    }
    setSavingAuth(true);
    try {
      await api.createAutorizacion(token, authForm);
      setShowAuthForm(false);
      setAuthForm({ eventoId: '', eventoNombre: '', titulo: '', descripcion: '', tipo: 'otro', obligatoria: false });
      loadAll();
    } catch (err) { alert('Error: ' + err.message); }
    setSavingAuth(false);
  }

  async function deleteAuth(id) {
    if (!window.confirm('Eliminar autorizacion y todas sus firmas?')) return;
    try {
      await api.deleteAutorizacion(token, id);
      loadAll();
    } catch (e) { alert('Error: ' + e.message); }
  }

  // Filtrado
  const filteredEntries = entries.filter(e => !filterEvento || e.eventoId === filterEvento);
  const filteredAuth = autorizaciones.filter(a => !filterEvento || a.eventoId === filterEvento);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--mid)' }}>Cargando...</div>;

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '.8rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--white)' }}>Portal familias</h1>
          <p style={{ fontSize: '.85rem', color: 'var(--mid)' }}>Diario de actividades y autorizaciones</p>
        </div>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button className={`btn ${tab === 'diario' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('diario')}>📖 Diario</button>
          <button className={`btn ${tab === 'autorizaciones' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('autorizaciones')}>✍️ Autorizaciones</button>
        </div>
      </div>

      {/* Filtro de evento */}
      <div style={{ display: 'flex', gap: '.8rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="form-select" value={filterEvento} onChange={e => setFilterEvento(e.target.value)}
          style={{ maxWidth: 280 }}>
          <option value="">Todos los eventos</option>
          {eventos.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </select>
        {tab === 'diario' && (
          <button className="btn btn-primary" onClick={() => openDiarioForm()}>+ Nueva entrada</button>
        )}
        {tab === 'autorizaciones' && (
          <button className="btn btn-primary" onClick={() => setShowAuthForm(true)}>+ Nueva autorizacion</button>
        )}
      </div>

      {/* ── DIARIO TAB ── */}
      {tab === 'diario' && (
        <>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            <StatBox label="Total entradas" value={entries.length} color="var(--sage)" />
            <StatBox label="Publicadas" value={entries.filter(e => e.publicado).length} color="#22c55e" />
            <StatBox label="Borradores" value={entries.filter(e => !e.publicado).length} color="#f59e0b" />
          </div>

          {filteredEntries.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--mid)' }}>
              No hay entradas en el diario{filterEvento ? ' para este evento' : ''}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.7rem' }}>
              {filteredEntries.map(entry => (
                <div key={entry.id} style={{
                  background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,171,138,.15)',
                  borderLeft: `4px solid ${entry.publicado ? '#22c55e' : '#f59e0b'}`,
                  borderRadius: 'var(--radius)', padding: '1rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '.5rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.3rem' }}>
                        <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--white)' }}>{entry.titulo}</span>
                        <span className={`badge ${entry.publicado ? 'badge-green' : 'badge-amber'}`} style={{ fontSize: '.65rem' }}>
                          {entry.publicado ? 'Publicado' : 'Borrador'}
                        </span>
                      </div>
                      <div style={{ fontSize: '.75rem', color: 'var(--sage)' }}>{entry.eventoNombre}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '.75rem', color: 'var(--mid)' }}>
                        {entry.fecha ? new Date(entry.fecha + 'T00:00:00').toLocaleDateString('es-ES', {
                          weekday: 'short', day: 'numeric', month: 'short'
                        }) : ''}
                      </div>
                      <div style={{ fontSize: '.68rem', color: 'var(--mid)' }}>por {entry.creadoPorNombre}</div>
                    </div>
                  </div>
                  <p style={{ margin: '.5rem 0 0', fontSize: '.85rem', color: 'var(--white)', lineHeight: 1.5,
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>{entry.contenido}</p>
                  <div style={{ marginTop: '.6rem', display: 'flex', gap: '.4rem' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openDiarioForm(entry)}>Editar</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => togglePublicado(entry)}>
                      {entry.publicado ? 'Despublicar' : 'Publicar'}
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ color: '#ef4444' }} onClick={() => deleteDiario(entry.id)}>
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── AUTORIZACIONES TAB ── */}
      {tab === 'autorizaciones' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            <StatBox label="Total autorizaciones" value={autorizaciones.length} color="var(--sage)" />
            <StatBox label="Obligatorias" value={autorizaciones.filter(a => a.obligatoria).length} color="#ef4444" />
            <StatBox label="Total firmas" value={autorizaciones.reduce((s, a) => s + (a.firmasAceptadas || 0), 0)} color="#22c55e" />
          </div>

          {filteredAuth.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--mid)' }}>
              No hay autorizaciones{filterEvento ? ' para este evento' : ''}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.7rem' }}>
              {filteredAuth.map(aut => (
                <div key={aut.id} style={{
                  background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,171,138,.15)',
                  borderLeft: `4px solid ${aut.obligatoria ? '#ef4444' : 'var(--sage)'}`,
                  borderRadius: 'var(--radius)', padding: '1rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '.5rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.3rem' }}>
                        <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--white)' }}>{aut.titulo}</span>
                        <span className="badge" style={{ fontSize: '.65rem',
                          background: (AUTH_TIPOS.find(t => t.value === aut.tipo)?.label ? '#3b82f622' : '#6b728022'),
                          color: '#3b82f6', border: '1px solid #3b82f644',
                        }}>{AUTH_TIPOS.find(t => t.value === aut.tipo)?.label || aut.tipo}</span>
                        {aut.obligatoria && <span className="badge badge-danger" style={{ fontSize: '.65rem' }}>Obligatoria</span>}
                      </div>
                      <div style={{ fontSize: '.75rem', color: 'var(--sage)' }}>{aut.eventoNombre}</div>
                      {aut.descripcion && (
                        <p style={{ margin: '.4rem 0 0', fontSize: '.82rem', color: 'var(--mid)', lineHeight: 1.4 }}>{aut.descripcion}</p>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--sage)', fontFamily: "'Fraunces',serif" }}>
                        {aut.firmasAceptadas || 0}
                      </div>
                      <div style={{ fontSize: '.65rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em' }}>firmas</div>
                    </div>
                  </div>
                  <div style={{ marginTop: '.6rem', display: 'flex', gap: '.4rem' }}>
                    <button className="btn btn-ghost btn-sm" style={{ color: '#ef4444' }} onClick={() => deleteAuth(aut.id)}>
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── DIARIO FORM MODAL ── */}
      {showDiarioForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowDiarioForm(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div className="section-label">{editingEntry ? 'Editar entrada' : 'Nueva entrada del diario'}</div>
              <button className="modal-close" onClick={() => setShowDiarioForm(false)}>x</button>
            </div>
            <form onSubmit={handleDiarioSubmit}>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Evento <span className="req">*</span></label>
                  <select className="form-select" value={diarioForm.eventoId}
                    disabled={!!editingEntry}
                    onChange={e => {
                      const ev = eventos.find(ev => ev.id === e.target.value);
                      setDiarioForm(p => ({ ...p, eventoId: e.target.value, eventoNombre: ev?.name || '' }));
                    }} required>
                    <option value="">Selecciona evento</option>
                    {eventos.filter(e => e.status === 'active').map(ev => (
                      <option key={ev.id} value={ev.id}>{ev.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha</label>
                  <input className="form-input" type="date" value={diarioForm.fecha}
                    onChange={e => setDiarioForm(p => ({ ...p, fecha: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Titulo <span className="req">*</span></label>
                <input className="form-input" value={diarioForm.titulo}
                  onChange={e => setDiarioForm(p => ({ ...p, titulo: e.target.value }))} required
                  placeholder="Ej: Dia 3 - Excursion al rio" />
              </div>
              <div className="form-group">
                <label className="form-label">Contenido <span className="req">*</span></label>
                <textarea className="form-textarea" value={diarioForm.contenido} rows={6}
                  onChange={e => setDiarioForm(p => ({ ...p, contenido: e.target.value }))} required
                  placeholder="Cuenta a las familias como ha ido el dia..." />
              </div>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="toggle-wrap">
                  <input type="checkbox" checked={diarioForm.publicado}
                    onChange={e => setDiarioForm(p => ({ ...p, publicado: e.target.checked }))} />
                  <span className="toggle-track"><span className="toggle-thumb" /></span>
                  <span style={{ fontSize: '.88rem' }}>Publicar (visible para familias)</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '.8rem' }}>
                <button type="button" className="btn btn-ghost btn-full" onClick={() => setShowDiarioForm(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary btn-full" disabled={savingDiario}>
                  {savingDiario ? 'Guardando...' : editingEntry ? 'Guardar cambios' : 'Publicar entrada'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── AUTORIZACION FORM MODAL ── */}
      {showAuthForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAuthForm(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div className="section-label">Nueva autorizacion</div>
              <button className="modal-close" onClick={() => setShowAuthForm(false)}>x</button>
            </div>
            <form onSubmit={handleAuthSubmit}>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Evento <span className="req">*</span></label>
                  <select className="form-select" value={authForm.eventoId} onChange={e => {
                    const ev = eventos.find(ev => ev.id === e.target.value);
                    setAuthForm(p => ({ ...p, eventoId: e.target.value, eventoNombre: ev?.name || '' }));
                  }} required>
                    <option value="">Selecciona evento</option>
                    {eventos.filter(e => e.status === 'active').map(ev => (
                      <option key={ev.id} value={ev.id}>{ev.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={authForm.tipo}
                    onChange={e => setAuthForm(p => ({ ...p, tipo: e.target.value }))}>
                    {AUTH_TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Titulo <span className="req">*</span></label>
                <input className="form-input" value={authForm.titulo}
                  onChange={e => setAuthForm(p => ({ ...p, titulo: e.target.value }))} required
                  placeholder="Ej: Autorizacion de salida excursion" />
              </div>
              <div className="form-group">
                <label className="form-label">Descripcion</label>
                <textarea className="form-textarea" value={authForm.descripcion} rows={4}
                  onChange={e => setAuthForm(p => ({ ...p, descripcion: e.target.value }))}
                  placeholder="Texto completo de la autorizacion que la familia debera aceptar..." />
              </div>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="toggle-wrap">
                  <input type="checkbox" checked={authForm.obligatoria}
                    onChange={e => setAuthForm(p => ({ ...p, obligatoria: e.target.checked }))} />
                  <span className="toggle-track"><span className="toggle-thumb" /></span>
                  <span style={{ fontSize: '.88rem' }}>Obligatoria (la familia debe firmar para participar)</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '.8rem' }}>
                <button type="button" className="btn btn-ghost btn-full" onClick={() => setShowAuthForm(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary btn-full" disabled={savingAuth}>
                  {savingAuth ? 'Guardando...' : 'Crear autorizacion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,171,138,.15)',
      borderRadius: 'var(--radius)', padding: '1rem', textAlign: 'center',
    }}>
      <div style={{ fontSize: '1.8rem', fontWeight: 800, color, fontFamily: "'Fraunces',serif" }}>{value}</div>
      <div style={{ fontSize: '.72rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</div>
    </div>
  );
}
